import { useEffect, useId, useState } from "react";
import { Field } from "react-final-form";
import { texts } from "../../lang";
import type { FlexDirection } from "../../styling";
import type { FieldOption } from "../../types/form";
import { isRequired } from "../../utils";
import { Stack } from "../stack";
import { Text } from "../typography";
import { Fieldset } from "./components/fieldset";
import { Legend } from "./components/legend";
import { RadioButton } from "./components/radio";
import { FormControlLabel } from "./form-control-label";
import { FormHelperText } from "./form-helper-text";

type Props = {
	options: Array<FieldOption>;
	name: string;
	legend?: string;
	initialValue?: string;
	required?: boolean;
	direction?: FlexDirection;
	description?: string;
};

export const RadioGroupField = ({
	name,
	legend,
	options,
	initialValue,
	description,
	required = true,
	direction = "col",
}: Props) => {
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);

	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			{!!legend && <Legend>{legend}</Legend>}
			<Stack gap={2} align="start">
				{!!description && <Text className="text-dense">{description}</Text>}
				<Field<string>
					name={name}
					initialValue={initialValue}
					validate={required ? (v) => isRequired(v, texts.validation.chooseOne) : undefined}
				>
					{({ input, meta }) => {
						// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
						useEffect(() => {
							setHasSubmitError(!!meta.submitFailed && !!meta.error);
						}, [meta]);
						return (
							<Stack gap={2} direction={direction}>
								{options.map((option) => {
									return (
										<FormControlLabel label={option.label} key={option.value}>
											<RadioButton
												checked={input.value === option.value}
												name={input.name}
												value={option.value}
												onChange={() => input.onChange(option.value)}
											/>
										</FormControlLabel>
									);
								})}
								{meta.error && meta.touched && (
									<FormHelperText error id={describedById}>
										{meta.error}
									</FormHelperText>
								)}
							</Stack>
						);
					}}
				</Field>
			</Stack>
		</Fieldset>
	);
};
